"use client";

import { useConversionHyperParameters } from "@/lib/zustand/useConversionHyperParameters";
import { ArtistInstrumentModelMixedModelNoDates } from "@/types/defs/artistInstrumentMixedModelNoDates";
import { IsolationBadges } from "@/types/defs/audioQuality/isolationBadges";
import { PitchRecommendation } from "@/types/defs/audioQuality/pitchRecommendation";
import { AudioInputState } from "@/types/types";
import { getAudioIsolationRecommendations } from "@/utils/audio/quality/getAudioIsolationRecommendations";
import { getModelPitchRange } from "@/utils/audio/quality/getModelPitchRange";
import { computePitchWindow } from "@/utils/audio/quality/pitchWindow/computePitchWindow";
import { isAudioInputFile } from "@/utils/audioInput/is/isAudioInputFile";

// Single source of audio-quality-derived state for the conversion flow. Body reads top to bottom: store reads → input extraction → pure derivations → return. The auto-applied isolation toggles (`removeInstrumental`, `deReverbDeEcho`) aren't mirrored into the store from the recommendation. The store holds the user's choice (or `null` for "untouched"), and the effective value is computed here as `userChoice ?? recommendation`.
export const useAudioQuality = (
  activeConversionInputs: AudioInputState[],
  isInstrumentSelected: boolean,
  selectedModel: ArtistInstrumentModelMixedModelNoDates | null
): {
  badges: IsolationBadges;
  pitchRecommendation: PitchRecommendation;
  removeInstrumental: boolean;
  deReverbDeEcho: boolean;
  shouldShowAutoSettingsBadge: boolean;
} => {
  const userRemoveInstrumental = useConversionHyperParameters(
    (state) => state.removeInstrumental
  );
  const userDeReverbDeEcho = useConversionHyperParameters(
    (state) => state.deReverbDeEcho
  );

  const fileInputs = activeConversionInputs.filter(isAudioInputFile);
  const allAudioQualityChecks = fileInputs.map(
    (input) => input.selectedFileCover.audioQualityCheck
  );
  const primaryAudioQualityCheck =
    (
      activeConversionInputs.length === 1 &&
      isAudioInputFile(activeConversionInputs[0])
    ) ?
      activeConversionInputs[0].selectedFileCover.audioQualityCheck
    : null;

  const badges = getAudioIsolationRecommendations(allAudioQualityChecks);
  const modelPitchRange = getModelPitchRange({
    selectedModel,
    shouldGetArtistsSweetSpot: false,
  });

  const pitchRecommendation = computePitchWindow(
    primaryAudioQualityCheck,
    modelPitchRange
  );

  // Effective toggle values: user choice takes precedence; otherwise the recommendation drives. Instrument models opt out of auto-applying.
  const instrumentalAutoApplied =
    !isInstrumentSelected && badges.instrumental === "autoApplied";
  const reverbAutoApplied =
    !isInstrumentSelected && badges.reverb === "autoApplied";
  const removeInstrumental = userRemoveInstrumental ?? instrumentalAutoApplied;
  const deReverbDeEcho = userDeReverbDeEcho ?? reverbAutoApplied;

  const isRecommendedSettingsTouched =
    userRemoveInstrumental !== null || userDeReverbDeEcho !== null;
  const shouldShowAutoSettingsBadge =
    !isInstrumentSelected &&
    !isRecommendedSettingsTouched &&
    (instrumentalAutoApplied || reverbAutoApplied);

  return {
    badges,
    pitchRecommendation,
    removeInstrumental,
    deReverbDeEcho,
    shouldShowAutoSettingsBadge,
  };
};
